/* istanbul ignore file */

import { useCallback } from 'react';
import { useClient } from 'urql';

import { useOrders } from '@order/features/orders';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import {
  OrdersV2ForReorderV2Document,
  type OrdersV2ForReorderV2Query,
  type OrdersV2ForReorderV2QueryVariables,
} from '../../graphql/ReorderOrdersV2.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook for fetching the user's most recent orders with pagination support
 * and line item reorder availability checking.
 */
export const useReorderOrdersV2 = (restaurantId: number | undefined) => {
  const client = useClient();
  const isLoggedIn = useIsLoggedIn();

  // ─── Queries ─────────────────────────────────────────────────────────

  const ordersQuery = useCallback(
    async (input: OrdersV2ForReorderV2QueryVariables['input']) => {
      const queryOrders = client.query<
        OrdersV2ForReorderV2Query,
        OrdersV2ForReorderV2QueryVariables
      >;

      return queryOrders(
        OrdersV2ForReorderV2Document,
        { input, restaurantId: restaurantId ?? 0 },
        { requestPolicy: 'network-only' },
      ).toPromise();
    },
    [client.query, restaurantId],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const fetchOrders = useCallback<FetchOrders>(
    async (context) => {
      const { page, ordersPerPage } = context;

      if (!isLoggedIn) return { orders: [], lastPage: page };

      const { data } = await ordersQuery({ take: ordersPerPage, page });

      const ordersData = data?.ordersV2;
      const hasResolvedOrders =
        ordersData?.__typename === 'OrdersResponseSuccess';

      if (!hasResolvedOrders) {
        return {
          orders: [],
          lastPage: page,
          includeCanceledOrders: true,
          includePlacedOrders: true,
        };
      }

      // NOTE: BE pagination utilizes a zero-based counter (e.g., 0 == 1, 1 == 2, etc.),
      //       therefore, we increase the number by one to match the state machine model.
      const lastPage = (ordersData.pagination.last ?? 0) + 1;

      return { orders: ordersData.orders, lastPage };
    },
    [isLoggedIn, ordersQuery],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useOrders<Order>({ fetchOrders });
};

// ─── Types ───────────────────────────────────────────────────────────────────

type FetchOrders = UseOrdersParams['fetchOrders'];

type UseOrdersParams = Parameters<typeof useOrders<Order>>[0];

type Order = Orders[number];

type Orders = NonNullable<
  Extract<
    OrdersV2ForReorderV2Query['ordersV2'],
    { __typename: 'OrdersResponseSuccess' }
  >['orders']
>;
